import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Alert } from "react-bootstrap";
import { HiX } from "react-icons/hi";

export type TBasicAlertRef = {
  setMessage: (message: string) => void;
  show: (options?: Partial<Pick<TBasicAlertProps, "message" | "type" | "dismissable">>) => void;
  hide: () => void;
};

export type TBasicAlertProps = {
  type?: "success" | "info" | "danger" | "warning";
  message?: string | JSX.Element | JSX.Element[];
  /** Initial state whether this basic alert is shown or hidden */
  show?: boolean;
  fade?: boolean;
  dismissable?: boolean;
  className?: string;
  classNameChildren?: string;
  noGap?: boolean;
};

const BasicAlert = forwardRef<TBasicAlertRef, TBasicAlertProps>(({
  type = "info",
  message = "...",
  show = false,
  className = "",
  classNameChildren = "",
  fade = true,
  dismissable = true,
  noGap = false
}, ref) => {
  const [alertType, setAlertType] = useState<TBasicAlertProps["type"]>(type);
  const [alertShow, setAlertShow] = useState(show);
  const [alertMessage, setAlertMessage] = useState<any>(message);
  const [alertDismissable, setAlertDismissable] = useState<boolean>(dismissable);

  const showRef = useRef<boolean>(show);

  useEffect(() => {
    setAlertMessage(message);
  }, [message]);

  useEffect(() => {
    showRef.current = alertShow;
  }, [alertShow]);

  useImperativeHandle(ref, () => ({
    setMessage(userMessage) {
      setAlertMessage(userMessage);
    },
    show(opts) {
      const { message, type, dismissable: userDismissable } = opts || {};

      if (typeof type === "string") {
        setAlertType(type);
      }

      if (typeof message !== "undefined") {
        setAlertMessage(message);
      }

      if (typeof userDismissable === "boolean") {
        setAlertDismissable(userDismissable);
      } else {
        setAlertDismissable(dismissable);
      }

      if (showRef.current) {
        setAlertShow(false);
        setTimeout(() => setAlertShow(true), 100);
      } else {
        setAlertShow(true);
      }
    },
    hide() {
      setAlertShow(false);
    }
  }), []);

  return <div className={`basic-alert ${dismissable ? "basic-alert--dismissable" : ""} ${noGap ? "basic-alert--no-gap" : ""} ${className}`.trim()}>
    <Alert show={alertShow} variant={alertType} transition={fade} className={classNameChildren}>
      {alertDismissable && <>
        <button type="button" className="ba__btn-close" title="Close" onClick={(e) => {
          e.preventDefault();
          setAlertShow(false);
        }}>
          <HiX />
        </button>
      </>}
      {alertMessage}
    </Alert>
  </div>;
});

export default BasicAlert;
